<template>
  <HrPageLayout
    title="Arrears and Recoveries"
    subtitle="Summary of arrears and recoveries by type and department"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Arrears and Recoveries' },
      { label: 'Overview' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="load">
        <i class="bx bx-refresh"></i> Refresh
      </button>
    </template>

    <div class="ar-wrap" v-if="totals">
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Arrears cases</div>
          <div class="stat-value">{{ totals.arrearsCount }}</div>
          <div class="stat-sub">Principal KES {{ money(totals.arrearsPrincipal) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Arrears balance</div>
          <div class="stat-value">{{ money(totals.arrearsBalance) }}</div>
          <div class="stat-sub">Paid KES {{ money(totals.arrearsPaid) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Recoveries cases</div>
          <div class="stat-value">{{ totals.recoveriesCount }}</div>
          <div class="stat-sub">Principal KES {{ money(totals.recoveriesPrincipal) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Recoveries balance</div>
          <div class="stat-value">{{ money(totals.recoveriesBalance) }}</div>
          <div class="stat-sub">Recovered KES {{ money(totals.recoveriesPaid) }}</div>
        </div>
      </div>

      <div class="charts-row">
        <div class="hr-chart-card">
          <h5>Arrears by type</h5>
          <div class="canvas-wrap"><canvas ref="pieAr"></canvas></div>
        </div>
        <div class="hr-chart-card">
          <h5>Recoveries by type</h5>
          <div class="canvas-wrap"><canvas ref="pieRec"></canvas></div>
        </div>
      </div>
      <div class="hr-chart-card">
        <h5>Amount by department</h5>
        <div class="canvas-wrap tall"><canvas ref="barDept"></canvas></div>
      </div>
    </div>
    <p v-else-if="!loading" class="text-muted">No data yet. Record arrears or recoveries to populate this overview.</p>
  </HrPageLayout>
</template>

<script>
import Chart from "chart.js/auto";
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "ArrearsRecoveriesOverview",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      totals: null,
      pieArrears: [],
      pieRecoveries: [],
      byDepartment: [],
      charts: {},
    };
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    money(n) {
      return (Number(n) || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async load() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "arGetOverview");
      try {
        const res = await execute(fd, authUrl);
        const d = res?.data?.data || {};
        this.totals = d.totals || null;
        this.pieArrears = d.pieArrears || [];
        this.pieRecoveries = d.pieRecoveries || [];
        this.byDepartment = d.byDepartment || [];
        this.$nextTick(() => this.render());
      } finally {
        this.loading = false;
      }
    },
    render() {
      Object.values(this.charts).forEach((c) => c && c.destroy());
      this.charts = {};
      const doughnut = (el, items, emptyLabel) => {
        if (!el) return null;
        const labels = items.length ? items.map((x) => x.label) : [emptyLabel];
        const data = items.length ? items.map((x) => x.value) : [1];
        return new Chart(el, {
          type: "doughnut",
          data: {
            labels,
            datasets: [{ data, backgroundColor: ["#0c4a6e", "#b45309", "#047857", "#7c3aed", "#be123c", "#0369a1", "#64748b"] }],
          },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom" } } },
        });
      };
      this.charts.a = doughnut(this.$refs.pieAr, this.pieArrears, "No arrears");
      this.charts.b = doughnut(this.$refs.pieRec, this.pieRecoveries, "No recoveries");
      const el = this.$refs.barDept;
      if (el) {
        this.charts.c = new Chart(el, {
          type: "bar",
          data: {
            labels: this.byDepartment.map((d) => d.department),
            datasets: [{ label: "Principal (KES)", data: this.byDepartment.map((d) => d.amount), backgroundColor: "#0c4a6e" }],
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } },
        });
      }
    },
  },
};
</script>

<style scoped>
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 16px; }
.stat-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; }
.stat-label { font-size: 12px; color: #64748b; }
.stat-value { font-size: 22px; font-weight: 700; color: #0f172a; }
.stat-sub { font-size: 12px; color: #475569; }
.charts-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
.hr-chart-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; }
.hr-chart-card h5 { font-size: 14px; margin: 0 0 8px; }
.canvas-wrap { height: 260px; position: relative; }
.canvas-wrap.tall { height: 320px; }
.canvas-wrap canvas { width: 100% !important; height: 100% !important; }
@media (max-width: 900px) { .charts-row { grid-template-columns: 1fr; } }
</style>
